import type { IngredientFormData } from '@/api/ingredients/ingredientMutations';
import {
  createIngredientMutation,
  deleteIngredientMutation,
  updateIngredientMutation,
} from '@/api/ingredients/ingredientMutations';
import {
  getAdminIngredientQuery,
  getAdminIngredientsQuery,
} from '@/api/ingredients/ingredientQueries';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { Supplement } from '@/domain/Supplement';
import { getStorageUrl } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { OurSupplementFormDialog } from './OurSupplementFormDialog';

export const OurSupplements = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedSupplementId, setSelectedSupplementId] = useState<
    number | null
  >(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplementIdToDelete, setSupplementIdToDelete] = useState<
    number | null
  >(null);

  const { data: response, isLoading } = useQuery(
    getAdminIngredientsQuery(page),
  );

  const { data: supplementDetails } = useQuery({
    ...getAdminIngredientQuery(selectedSupplementId || 0),
    enabled: !!selectedSupplementId && formDialogOpen,
  });

  const supplements = response?.data || [];
  const meta = response?.meta;

  const createMutation = useMutation({
    ...createIngredientMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminIngredients.all(),
      });
      setFormDialogOpen(false);
      toast.success('Supplement created successfully');
    },
  });

  const updateMutation = useMutation({
    ...updateIngredientMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminIngredients.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminIngredients.single(selectedSupplementId!),
      });
      setFormDialogOpen(false);
      setSelectedSupplementId(null);
      toast.success('Supplement updated successfully');
    },
  });

  const deleteMutation = useMutation({
    ...deleteIngredientMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminIngredients.all(),
      });
      setSelectedSupplementId(null);
      setFormDialogOpen(false); // Close dialog after deletion
      toast.success('Supplement deleted successfully');
    },
  });

  const handleDeleteItem = (id: number) => {
    setSupplementIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (supplementIdToDelete !== null) {
      deleteMutation.mutate(supplementIdToDelete);
      setDeleteDialogOpen(false);
      setSupplementIdToDelete(null);
    }
  };

  const handleAddItem = () => {
    setDialogMode('add');
    setSelectedSupplementId(null);
    setFormDialogOpen(true);
  };

  const handleEditItem = (id: number) => {
    setDialogMode('edit');
    setSelectedSupplementId(id);
    setFormDialogOpen(true);
  };

  const handleFormSubmit = (data: IngredientFormData) => {
    if (dialogMode === 'add') {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  return (
    <>
      <DashboardTableAccordion
        value="our-supplements"
        title="Our supplements"
        data={supplements}
        isLoading={isLoading}
        loadingMessage="Loading supplements..."
        paginationMeta={meta}
        onPageChange={setPage}
        renderHeader={() => (
          <>
            <TableHead className="w-24">Image</TableHead>
            <TableHead>Ingredient</TableHead>
            <TableHead className="w-24" />
          </>
        )}
        renderRow={(supplement: Supplement) => (
          <>
            <TableCell>
              <div className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[10px] italic">
                {supplement.image_url ? (
                  <img
                    src={getStorageUrl(supplement.image_url)}
                    alt=""
                    className="size-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                ) : (
                  'img'
                )}
              </div>
            </TableCell>
            <TableCell>{supplement.name}</TableCell>
            <TableCell className="text-right">
              <RelationActionButton
                onClick={() => handleEditItem(supplement.id)}
              >
                Modify
              </RelationActionButton>
            </TableCell>
          </>
        )}
        footerActions={
          <Button variant="primary" size="md" onClick={handleAddItem}>
            Add New Item
          </Button>
        }
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete supplement"
        description="Are you sure you want to delete this supplement?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />

      <OurSupplementFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        supplement={dialogMode === 'edit' ? supplementDetails : null}
        mode={dialogMode}
        onSubmit={handleFormSubmit}
        onDelete={handleDeleteItem}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
};
